import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./AddressModal.module.css";
import Modal from "components/Modal";
import TextInput from "components/TextInput";
import SelectDropdown from "../SelectDropdown";
import Button from "Shared/Button";
import { useTranslation } from "react-i18next";

const COUNTRY_LABEL_TO_CODE = {
  "United States": "US",
  Canada: "CA",
  Jamaica: "JM",
  "Cayman Islands": "KY",
};

const COUNTRY_CODE_TO_LABEL = {
  US: "United States",
  CA: "Canada",
  JM: "Jamaica",
  KY: "Cayman Islands",
};

const POSTAL_CODE_OPTIONAL_COUNTRIES = ["JM"];

const normalizeCountryCode = (value) => {
  if (!value) return "US";
  if (COUNTRY_LABEL_TO_CODE[value]) return COUNTRY_LABEL_TO_CODE[value];
  return value;
};

const countryLabelFromCode = (value) =>
  COUNTRY_CODE_TO_LABEL[value] ?? value ?? "";

/**
 * Interactive component
 *
 * Modal used to update and add addresses.
 */

export function AddressModal({
  address,
  isOpen,
  onClose,
  onValidate,
  title,
  errorMessage = "",
}) {
  const [addressData, setAddressData] = useState({
    country: "",
    nickname: "",
    recipientName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
  });

  useEffect(() => {
    if (!address) {
      setAddressData({
        country: "US",
        nickname: "",
        recipientName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
      });
      return;
    }

    setAddressData({
      country: normalizeCountryCode(address.country),
      nickname: address.nickname ?? address.addressNickname ?? "",
      recipientName:
        address.recipientName ?? address.name ?? address.recipient ?? "",
      address1: address.address1 ?? address.line1 ?? "",
      address2: address.address2 ?? address.line2 ?? "",
      city: address.city ?? "",
      state: address.state ?? "",
      zip: address.zip ?? address.postalCode ?? "",
    });
  }, [address]);

  const isZipOptional = POSTAL_CODE_OPTIONAL_COUNTRIES.includes(
    addressData.country
  );

  const isFormValid = Boolean(
    addressData.country &&
      addressData.recipientName &&
      addressData.address1 &&
      addressData.city &&
      addressData.state &&
      (isZipOptional || addressData.zip)
  );

  const handleStateChange = (selectedState) => {
    const stateAbbreviation = selectedState.split(" - ")[0];
    setAddressData({ ...addressData, state: stateAbbreviation });
  };

  const { t } = useTranslation();
  const countryArray = [
    "United States",
    "Canada",
    "Jamaica",
    "Cayman Islands",
  ];

  const usStates = [
    "AL - Alabama",
    "AK - Alaska",
    "AZ - Arizona",
    "AR - Arkansas",
    "CA - California",
    "CO - Colorado",
    "CT - Connecticut",
    "DE - Delaware",
    "FL - Florida",
    "GA - Georgia",
    "HI - Hawaii",
    "ID - Idaho",
    "IL - Illinois",
    "IN - Indiana",
    "IA - Iowa",
    "KS - Kansas",
    "KY - Kentucky",
    "LA - Louisiana",
    "ME - Maine",
    "MD - Maryland",
    "MA - Massachusetts",
    "MI - Michigan",
    "MN - Minnesota",
    "MS - Mississippi",
    "MO - Missouri",
    "MT - Montana",
    "NE - Nebraska",
    "NV - Nevada",
    "NH - New Hampshire",
    "NJ - New Jersey",
    "NM - New Mexico",
    "NY - New York",
    "NC - North Carolina",
    "ND - North Dakota",
    "OH - Ohio",
    "OK - Oklahoma",
    "OR - Oregon",
    "PA - Pennsylvania",
    "RI - Rhode Island",
    "SC - South Carolina",
    "SD - South Dakota",
    "TN - Tennessee",
    "TX - Texas",
    "UT - Utah",
    "VT - Vermont",
    "VA - Virginia",
    "WA - Washington",
    "WV - West Virginia",
    "WI - Wisconsin",
    "WY - Wyoming",
    "DC - District of Columbia",
    "AS - American Samoa",
    "GU - Guam",
    "MP - Northern Mariana Islands",
    "PR - Puerto Rico",
    "VI - U.S. Virgin Islands",
  ];

  const canadianProvinces = [
    "AB - Alberta",
    "BC - British Columbia",
    "MB - Manitoba",
    "NB - New Brunswick",
    "NL - Newfoundland and Labrador",
    "NS - Nova Scotia",
    "NT - Northwest Territories",
    "NU - Nunavut",
    "ON - Ontario",
    "PE - Prince Edward Island",
    "QC - Quebec",
    "SK - Saskatchewan",
    "YT - Yukon",
  ];

  const jamaicanParishes = [
    "CL - Clarendon",
    "HA - Hanover",
    "KI - Kingston",
    "MA - Manchester",
    "PO - Portland",
    "SD - Saint Andrew",
    "SN - Saint Ann",
    "SC - Saint Catherine",
    "SE - Saint Elizabeth",
    "SJ - Saint James",
    "SM - Saint Mary",
    "ST - Saint Thomas",
    "TR - Trelawny",
    "WE - Westmoreland",
  ];

  const caymanDistricts = [
    "BT - Bodden Town",
    "CB - Cayman Brac",
    "EE - East End",
    "GC - Grand Cayman",
    "GT - George Town",
    "LC - Little Cayman",
    "NS - North Side",
    "WB - West Bay",
  ];

  const stateArrayByCountry = {
    US: usStates,
    CA: canadianProvinces,
    JM: jamaicanParishes,
    KY: caymanDistricts,
  };
  const stateArray = stateArrayByCountry[addressData.country] ?? usStates;
  const selectedStateOption = stateArray.find(
    (option) => option.split(" - ")[0] === addressData.state
  );
  const stateLabel =
    addressData.country === "CA"
      ? t("Select Province*")
      : addressData.country === "JM"
      ? t("Select Parish*")
      : addressData.country === "KY"
      ? t("Select District*")
      : t("Select State*");

  const handleSubmit = (event) => {
    event.preventDefault();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        className={styles.modal}
        title={title}
        children={
          <>
            <div className={styles.container}>
              <div>
                <h1 className={styles.title}>{title}</h1>
              </div>
              <form onSubmit={handleSubmit}>
                <div className={styles.group}>
                  <div className={styles.formSelect}>
                    <SelectDropdown
                      options={countryArray}
                      onChange={(e) => {
                        setAddressData({
                          ...addressData,
                          country: normalizeCountryCode(e),
                          state: "",
                        });
                      }}
                      placeholder={
                        countryLabelFromCode(addressData.country) ||
                        t("Select*")
                      }
                      disabled={false}
                      dropdownRight={false}
                      isScrollable={true}
                      id="country"
                      name="country"
                    />
                  </div>
                  <div className={styles.formItem}>
                    <TextInput
                      label={""}
                      value={addressData.nickname}
                      setValue={(e) =>
                        setAddressData({
                          ...addressData,
                          nickname: e.target.value,
                        })
                      }
                      placeholder={
                        addressData.nickname || t("Address Nickname (Optional)")
                      }
                      type="text"
                      id="nickname"
                    />
                  </div>
                  <div className={styles.formItem}>
                    <TextInput
                      label={""}
                      value={addressData.recipientName}
                      setValue={(e) =>
                        setAddressData({
                          ...addressData,
                          recipientName: e.target.value,
                        })
                      }
                      placeholder={
                        addressData.recipientName || t("Recipient Name*")
                      }
                      type="text"
                      id="recipientName"
                    />
                  </div>
                  <div className={styles.formItem}>
                    <TextInput
                      label={""}
                      value={addressData.address1}
                      setValue={(e) =>
                        setAddressData({
                          ...addressData,
                          address1: e.target.value,
                        })
                      }
                      placeholder={addressData.address1 || t("Address*")}
                      type="text"
                      id="address1"
                    />
                  </div>
                  <div className={styles.formItem}>
                    <TextInput
                      label={""}
                      value={addressData.address2}
                      setValue={(e) =>
                        setAddressData({
                          ...addressData,
                          address2: e.target.value,
                        })
                      }
                      placeholder={
                        addressData.address2 || t("Suite, Apartment, etc. (Optional)")
                      }
                      type="text"
                      id="address2"
                    />
                  </div>
                  <div className={styles.formItem}>
                    <TextInput
                      label={""}
                      value={addressData.city}
                      setValue={(e) =>
                        setAddressData({ ...addressData, city: e.target.value })
                      }
                      placeholder={addressData.city || t("City*")}
                      type="text"
                      id="city"
                    />
                  </div>
                  <div className={styles.formSelect}>
                    <SelectDropdown
                      options={stateArray}
                      onChange={(e) => handleStateChange(e)}
                      placeholder={selectedStateOption || addressData.state || stateLabel}
                      disabled={false}
                      dropdownRight={false}
                      isScrollable={true}
                      id="state"
                      key={addressData.country}
                    />
                  </div>
                  <div className={styles.formItem}>
                    <TextInput
                      label={""}
                      value={addressData.zip}
                      setValue={(e) =>
                        setAddressData({
                          ...addressData,
                          zip: e.target.value,
                        })
                      }
                      placeholder={
                        addressData.zip ||
                        (isZipOptional
                          ? t("Zip Code (Optional)")
                          : t("Zip Code*"))
                      }
                      type="text"
                      id="zip"
                    />
                  </div>
                </div>
              </form>
              {errorMessage && (
                <div className={styles.error} role="alert">
                  {errorMessage}
                </div>
              )}
              <div className={styles.validate}>
                {/* Validate button handled on AddressList */}
                <Button
                  text={t("Validate Address")}
                  onClick={() =>
                    onValidate({
                      ...addressData,
                      recipient: addressData.recipientName,
                      name: addressData.recipientName,
                    })
                  }
                  className="selectBtn"
                  disabled={!isFormValid}
                />
              </div>
              <div className={styles.cancel}>
                {/* Cancel button handled on AddressList */}
                <Button
                  text={t("Cancel")}
                  onClick={onClose}
                  className="closeBtn"
                />
              </div>
            </div>
          </>
        }
      />
    </>
  );
}

AddressModal.propTypes = {
  address: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
};

export default AddressModal;
